import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { DocumentCheckIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { getNodeColors } from '../utils/nodeColors';
import FlintSignLogo from '../../../assets/fs_dark_logo.svg';

const FlintSignNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const nodeColors = getNodeColors('flintsign_sign');

  const apiKey = data.config?.apiKey || 'Nincs beállítva';
  const certificateId = data.config?.certificateId || 'Nincs beállítva';
  const inputFile = data.config?.inputFile || 'Nincs megadva';
  const signaturePosition = data.config?.signaturePosition || 'bottom-right';
  const signatureReason = data.config?.signatureReason || 'Digitális aláírás';
  
  // API kulcs státusz
  const hasApiKey = apiKey && apiKey !== 'Nincs beállítva';
  const hasCertificate = certificateId && certificateId !== 'Nincs beállítva';
  const hasInputFile = inputFile && inputFile !== 'Nincs megadva';
  
  // Pozíció megjelenítés
  const positionLabels: Record<string, string> = {
    'top-left': 'Bal felső',
    'top-right': 'Jobb felső',
    'bottom-left': 'Bal alsó',
    'bottom-right': 'Jobb alsó',
    'center': 'Középen'
  };
  
  // Fájl név rövidítés
  const fileDisplay = inputFile.length > 25 ? `${inputFile.substring(0, 22)}...` : inputFile;
  
  // Tanúsítvány ID rövidítés
  const certDisplay = certificateId.length > 20 ? `${certificateId.substring(0, 17)}...` : certificateId;

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r ${nodeColors.gradient.from} ${nodeColors.gradient.to} border-2 transition-all duration-200 ${
      selected ? nodeColors.border.selected + ' shadow-xl scale-105' : nodeColors.border.default
    }`}>
      <div className="flex items-center space-x-3">
        {/* FlintSign Logo */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-90 rounded-lg flex items-center justify-center p-1">
            <img 
              src={FlintSignLogo} 
              alt="FlintSign" 
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className={`text-xs ${nodeColors.text.secondary} truncate mt-1`}>
              {data.description}
            </div>
          )}
          
          {/* FlintSign Status */}
          <div className="flex items-center mt-1 space-x-1">
            <span className="text-sm text-white">🔐</span>
            <span className={`text-xs ${nodeColors.text.secondary}`}>
              {hasApiKey ? 'API konfigurálva' : 'API kulcs hiányzik'}
            </span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-700' : 
            (hasApiKey && hasCertificate && hasInputFile) ? 'bg-green-400' : 'bg-yellow-400'
          }`} />
        </div>
      </div>

      {/* Configuration preview */}
      <div className="mt-2 space-y-1">
        {/* Certificate Status */}
        <div className={`text-xs ${nodeColors.text.secondary} bg-black bg-opacity-20 px-2 py-1 rounded flex items-center justify-between`}>
          <div>
            <strong>Tanúsítvány:</strong>
            <span className="ml-1 truncate">{certDisplay}</span>
          </div>
          {hasCertificate ? <span className="text-xs">✅</span> : <span className="text-xs">❌</span>}
        </div>
        
        {/* Input File */}
        <div className={`text-xs ${nodeColors.text.secondary} bg-black bg-opacity-20 px-2 py-1 rounded`}>
          <strong>Fájl:</strong>
          <div className="truncate mt-1 opacity-90 font-mono">
            {fileDisplay}
          </div>
        </div>
        
        {/* Signature Settings */}
        <div className={`text-xs ${nodeColors.text.secondary} bg-black bg-opacity-20 px-2 py-1 rounded flex items-center justify-between`}>
          <div>
            <strong>Pozíció:</strong>
            <span className="ml-1">{positionLabels[signaturePosition] || signaturePosition}</span>
          </div>
          <span className="text-xs">✍️</span>
        </div>
        
        {/* Signature Reason */}
        <div className={`text-xs ${nodeColors.text.secondary} bg-black bg-opacity-20 px-2 py-1 rounded`}>
          <strong>Indok:</strong>
          <div className="truncate mt-1 opacity-90">
            {signatureReason}
          </div>
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-700 bg-opacity-30 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Signing status */}
      {data.executionData && (
        <div className="mt-2 text-xs text-emerald-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              data.executionData.success ? 'bg-green-400' : 'bg-red-400'
            }`} />
            <span>
              {data.executionData.success ? 'Sikeresen aláírva' : 'Aláírás sikertelen'}
            </span>
          </div>
          {data.executionData.signature_id && (
            <div className="font-mono text-xs mt-1 truncate">
              ID: {data.executionData.signature_id}
            </div>
          )}
          {data.executionData.signed_file && (
            <div className="text-xs mt-1 truncate">
              Fájl: {data.executionData.signed_file}
            </div>
          )}
          {data.executionData.api_response?.document_hash && (
            <div className="text-xs mt-1 font-mono opacity-75 truncate">
              Hash: {data.executionData.api_response.document_hash}
            </div>
          )}
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className={`w-3 h-3 ${nodeColors.handle} border-2 border-white`}
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className={`w-3 h-3 ${nodeColors.handle} border-2 border-white`}
      />
    </div>
  );
};

export default FlintSignNode;
